import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { User, Role } from 'app/auth/models';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  //public
  public currentUser: Observable<User>;

  //private
  private currentUserSubject: BehaviorSubject<User>;

  /**
   *
   * @param {HttpClient} _http
   * @param {ToastrService} _toastrService
   */
  constructor(private _http: HttpClient, private _toastrService: ToastrService) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Admin;
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Client;
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  login(email: string, password: string) {
    return this._http
      .post<any>(`${environment.api}/users/authenticate`, { email, password })
      .pipe(
        map(user => {
          // login successful if there's a jwt token in the response
          if (user && user.token) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));

            // Display welcome toast!
            setTimeout(() => {
              this._toastrService.success(
                'You have successfully logged in as an ' +
                  user.role +
                  ' user to Vuexy. Now you can start to explore. Enjoy! 🎉',
                '👋 Welcome, ' + user.firstName + '!',
                { toastClass: 'toast ngx-toastr', closeButton: true }
              );
            }, 2500);

            // notify
            this.currentUserSubject.next(user);
          }

          return user;
        })
      );
  }

  loginWithGoogle(credential: string) {
    const payload = this.decodeJwtPayload(credential);
    if (!payload) {
      console.error('Não foi possível decodificar o token Google');
      return;
    }
    const user: User = {
      id: payload.sub,
      email: payload.email || '',
      password: '',
      firstName: payload.given_name || '',
      lastName: payload.family_name || '',
      avatar: payload.picture || 'avatar-s-11.jpg',
      role: Role.Client,
      token: credential
    };
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);

    setTimeout(() => {
      // O aviso nao mostra titulo (ver styles.scss): o boas-vindas vai na propria mensagem.
      this._toastrService.success(
        'Bem-vindo, ' + user.firstName + '! Você entrou com o Google.',
        '',
        { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 4000 }
      );
    }, 2500);
  }

  // Login da loja so com telefone e nome (CON-3683). Nao ha verificacao do numero - sem
  // SMS/WhatsApp, o telefone identifica, nao autentica. Sem token: nada vai como Bearer.
  // `telefone` chega so com digitos (ver `telefoneDigitos`).
  loginComTelefone(telefone: string, nome: string) {
    const user: User = {
      id: Number(telefone),
      email: '',
      password: '',
      firstName: nome.trim(),
      lastName: '',
      avatar: '',
      role: Role.Client,
      origem: 'telefone',
      telefone,
    };
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private decodeJwtPayload(token: string): any {
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decodeURIComponent(escape(json)));
    } catch (e) {
      console.warn('Não foi possível decodificar o JWT do Google', e);
      return null;
    }
  }

  /**
   * User logout
   *
   */
  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    // notify
    this.currentUserSubject.next(null);
  }
}
